<script setup lang="ts">
/**
 * Reject a refund request — terminal "no money moves" outcome.
 * Operator provides a reason (min 5 chars per BE contract).
 */
import { computed, ref, watch } from 'vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import Message from '@uiKit/elements/Message.vue'
import FormField from '@uiKit/components/FormField.vue'
import { formatMoney } from '@shared/utils'

interface Props {
  visible: boolean
  amount: string
  currencyCode: string
  investorName: string
  loading?: boolean
  apiError?: string | null
}

const MIN_REASON_LENGTH = 5

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  apiError: null,
})

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'confirm', value: { reason: string }): void
}>()

const reason = ref('')
const localError = ref('')

const hasEditedSinceApiError = ref(false)
watch(
  () => props.apiError,
  () => {
    hasEditedSinceApiError.value = false
  },
)
const visibleApiError = computed(() =>
  props.apiError && !hasEditedSinceApiError.value ? props.apiError : '',
)

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) {
      reason.value = ''
      localError.value = ''
    }
  },
)

const trimmedReason = computed(() => reason.value.trim())
const amountLabel = computed(() => formatMoney(props.currencyCode, props.amount))

function handleConfirm() {
  if (trimmedReason.value.length < MIN_REASON_LENGTH) {
    localError.value = `Reason must be at least ${MIN_REASON_LENGTH} characters.`
    return
  }
  localError.value = ''
  emit('confirm', { reason: trimmedReason.value })
}

function handleInput(value: string) {
  reason.value = value
  if (localError.value) localError.value = ''
  hasEditedSinceApiError.value = true
}

function handleClose() {
  if (props.loading) return
  emit('update:visible', false)
}
</script>

<template>
  <Dialog
    :visible="visible"
    width="520px"
    max-height="80vh"
    :show-close-icon="!loading"
    :dismissable="!loading"
    @update:visible="(v) => !v && handleClose()"
  >
    <template #header>
      <Text type="page-title">Reject refund</Text>
    </template>

    <Box flex direction="col" gap="3">
      <Text type="body-sm" color="muted">
        Reject this refund?
        <Text is="span" type="body-sm" weight="semibold">{{ investorName }}</Text>
        will not receive their
        <Text is="span" type="body-sm" weight="semibold">{{ amountLabel }}</Text>
        back. The refund row will move to a terminal rejected state and no wallet credit will be
        written.
      </Text>

      <FormField label="Rejection reason" required :error="localError">
        <Textarea
          :model-value="reason"
          :rows="4"
          placeholder="e.g. Investor confirmed the order should not be refunded."
          :disabled="loading"
          @update:model-value="handleInput"
        />
      </FormField>

      <Message
        v-if="visibleApiError"
        color="danger"
        variant="outlined"
        size="small"
        :message="visibleApiError"
      />
    </Box>

    <template #footer>
      <Box flex justify="end" gap="2">
        <Button
          label="Cancel"
          variant="text"
          color="secondary"
          :disabled="loading"
          @click="handleClose"
        />
        <Button label="Reject refund" color="danger" :loading="loading" @click="handleConfirm" />
      </Box>
    </template>
  </Dialog>
</template>
